import { createSlice } from '@reduxjs/toolkit';

export const mapSlice = createSlice({
    name: 'map',
    initialState: {
        currentLocation: { lat: 61.498089, lng: 23.760746 },
        zoom: 14,
        routeCoordinates: null,
        isRoutingMode: false,
        isDrivingMode: false,
        currentBearing: 0,
        currentSpeed: 0,
        activeLight: null,
        activeLightDistance: null,
        isHudVisible: false,
        waypoints: [],
        routeProximity: 20, // meters, distance from route to light for it to be shown
        hudDistanceThreshold: 200, // meters, distance to light to show HUD (approx 15s @ 50km/h)
        hudTimeThreshold: 15, // seconds, time to light to show HUD
        isRerouting: false, // flag to indicate if rerouting is in progress
        intersectionState: {
            entryHeading: null,
            exitHeading: null
        },
        isHeadingUp: false, // toggle for map rotation
        offsetSettings: {
            default: { x: 0, y: 0 },
            hud: { x: 0, y: 0 }
        },
        isAutoOffset: true,
        hudDimensions: { width: 0, height: 0 }
    },
    reducers: {
        setCurrentLocation: (state, action) => {
            state.currentLocation = action.payload;
        },
        setZoom: (state, action) => {
            state.zoom = action.payload;
        },
        setRouteCoordinates: (state, action) => {
            state.routeCoordinates = action.payload;
        },
        setIsRoutingMode: (state, action) => {
            state.isRoutingMode = action.payload;
        },
        setIsDrivingMode: (state, action) => {
            state.isDrivingMode = action.payload;
        },
        setCurrentBearing: (state, action) => {
            state.currentBearing = action.payload;
        },
        setCurrentSpeed: (state, action) => {
            state.currentSpeed = action.payload;
        },
        setActiveLight: (state, action) => {
            state.activeLight = action.payload;
        },
        setActiveLightDistance: (state, action) => {
            state.activeLightDistance = action.payload;
        },
        setIsHudVisible: (state, action) => {
            state.isHudVisible = action.payload;
        },
        setWaypoints: (state, action) => {
            state.waypoints = action.payload;
        },
        setRouteProximity: (state, action) => {
            state.routeProximity = action.payload;
        },
        setHudDistanceThreshold: (state, action) => {
            state.hudDistanceThreshold = action.payload;
        },
        setHudTimeThreshold: (state, action) => {
            state.hudTimeThreshold = action.payload;
        },
        setIsRerouting: (state, action) => {
            state.isRerouting = action.payload;
        },
        setIntersectionState: (state, action) => {
            state.intersectionState = action.payload;
        },
        setIsHeadingUp: (state, action) => {
            state.isHeadingUp = action.payload;
        },
        setCenterOffset: (state, action) => {
            const mode = state.isHudVisible ? 'hud' : 'default';
            state.offsetSettings[mode] = action.payload;
        },
        setSpecificCenterOffset: (state, action) => {
            const { mode, offset } = action.payload;
            if (state.offsetSettings[mode]) {
                state.offsetSettings[mode] = offset;
            }
        },
        setIsAutoOffset: (state, action) => {
            state.isAutoOffset = action.payload;
        },
        setHudDimensions: (state, action) => {
            state.hudDimensions = action.payload;
        },
        resetMapState: (state) => {
            state.routeCoordinates = null;
            state.isRoutingMode = false;
            state.isDrivingMode = false;
            state.currentBearing = 0;
            state.currentSpeed = 0;
            state.activeLight = null;
            state.activeLightDistance = null;
            state.waypoints = [];
            state.isRerouting = false;
            state.intersectionState = { entryHeading: null, exitHeading: null };
        }
    }
});

export const {
    setCurrentLocation,
    setZoom,
    setRouteCoordinates,
    setIsRoutingMode,
    setIsDrivingMode,
    setCurrentBearing,
    setCurrentSpeed,
    setActiveLight,
    setActiveLightDistance,
    setIsHudVisible,
    setWaypoints,
    setRouteProximity,
    setHudDistanceThreshold,
    setHudTimeThreshold,
    setIsRerouting,
    setIntersectionState,
    setIsHeadingUp,
    setCenterOffset,
    setSpecificCenterOffset,
    setIsAutoOffset,
    setHudDimensions,
    resetMapState
} = mapSlice.actions;

export const selectCurrentLocation = state => state.map.currentLocation;
export const selectZoom = state => state.map.zoom;
export const selectRouteCoordinates = state => state.map.routeCoordinates;
export const selectIsRoutingMode = state => state.map.isRoutingMode;
export const selectIsDrivingMode = state => state.map.isDrivingMode;
export const selectCurrentBearing = state => state.map.currentBearing;
export const selectCurrentSpeed = state => state.map.currentSpeed;
export const selectActiveLight = state => state.map.activeLight;
export const selectActiveLightDistance = state => state.map.activeLightDistance;
export const selectIsHudVisible = state => state.map.isHudVisible;
export const selectWaypoints = state => state.map.waypoints;
export const selectRouteProximity = state => state.map.routeProximity;
export const selectHudDistanceThreshold = state => state.map.hudDistanceThreshold;
export const selectHudTimeThreshold = state => state.map.hudTimeThreshold;
export const selectIsRerouting = state => state.map.isRerouting;
export const selectIntersectionState = state => state.map.intersectionState;
export const selectIsHeadingUp = state => state.map.isHeadingUp;
export const selectIsAutoOffset = state => state.map.isAutoOffset;
export const selectHudDimensions = state => state.map.hudDimensions;
export const selectOffsetSettings = state => state.map.offsetSettings;
export const selectCenterOffset = state => {
    const mode = state.map.isHudVisible ? 'hud' : 'default';
    return state.map.offsetSettings[mode];
};

export default mapSlice.reducer;

